import { clientSDK } from "@istari-frontend/api-client";
import { ChangeRequestComponentType } from "@istari/istari-client";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { toChangeRequest } from "~/change-request/data-source/adapter/v2/to-change-request";
import type { ChangeRequestId } from "~/change-request/model/change-request";
import { DEFAULT_CACHE_TIME, QUERY_REFETCH_INTERVAL_MS_60S } from "~/constants";

export type ChangeRequestStatusFilter = {
  status: "open" | "merged" | "closed";
};

export type ChangeRequestActionParams = {
  systemId: string;
  changeRequestId: ChangeRequestId;
  comment: string | null;
};

// The close endpoint is a PUT that replaces title/description; the caller must echo the
// current values back or closing a change request would null them out.
export type ChangeRequestCloseParams = ChangeRequestActionParams & {
  title: string | undefined;
  description: string | undefined;
};

export type ChangeRequestMergeParams = ChangeRequestActionParams & {
  headPairToken: string | null;
};

export const changeRequestQueryKeys = {
  all: ["change-request"] as const,
  lists: () => [...changeRequestQueryKeys.all, "list"] as const,
  list: (systemId: string, filter: ChangeRequestStatusFilter) =>
    [...changeRequestQueryKeys.lists(), systemId, filter] as const,
  byId: (systemId: string, id: string) => [...changeRequestQueryKeys.all, "byId", systemId, id] as const,
  count: (systemId: string, status: "open" | "merged" | "closed") =>
    [...changeRequestQueryKeys.all, "count", systemId, status] as const,
  changeSummary: (systemId: string, id: ChangeRequestId) =>
    [...changeRequestQueryKeys.byId(systemId, id), "change-summary"] as const,
  changesInfinite: (systemId: string, id: ChangeRequestId) =>
    [...changeRequestQueryKeys.byId(systemId, id), "changes", "infinite"] as const,
  events: (systemId: string, id: ChangeRequestId) =>
    [...changeRequestQueryKeys.byId(systemId, id), "events", "infinite"] as const,
  changesProbe: (systemId: string, id: ChangeRequestId) =>
    [...changeRequestQueryKeys.byId(systemId, id), "changes", "probe"] as const,
  compare: (systemId: string, sourceSnapshotId: string, targetSnapshotId: string) =>
    [...changeRequestQueryKeys.all, "compare", systemId, sourceSnapshotId, targetSnapshotId] as const,
};

export const CHANGES_PAGE_SIZE = 50;
export const EVENTS_PAGE_SIZE = 50;

export type ChangesPageParam = { cursor: string | null; pinnedToken: string | null };

export const changeRequestQueryOptions = {
  list: (systemId: string, filter: ChangeRequestStatusFilter) =>
    infiniteQueryOptions({
      queryKey: changeRequestQueryKeys.list(systemId, filter),
      queryFn: ({ pageParam }) =>
        clientSDK.listChangeRequests(systemId, statusFilterToSdk(filter.status), null, null, null, pageParam, 20),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
      select: (data) => ({
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.map(toChangeRequest),
        })),
      }),
      enabled: !!systemId,
    }),
  byId: (systemId: string, id: string) =>
    queryOptions({
      queryKey: changeRequestQueryKeys.byId(systemId, id),
      queryFn: () => clientSDK.getChangeRequest(systemId, id),
      enabled: !!systemId && !!id,
    }),
  changeSummary: (systemId: string, id: ChangeRequestId) =>
    queryOptions({
      queryKey: changeRequestQueryKeys.changeSummary(systemId, id),
      queryFn: () => clientSDK.changeRequestChangeSummary(systemId, id),
      enabled: !!systemId && !!id,
      staleTime: DEFAULT_CACHE_TIME,
    }),
  changesInfinite: (systemId: string, id: ChangeRequestId) =>
    infiniteQueryOptions({
      queryKey: changeRequestQueryKeys.changesInfinite(systemId, id),
      queryFn: ({ pageParam }: { pageParam: ChangesPageParam }) =>
        clientSDK.changeRequestChanges(
          systemId,
          id,
          ChangeRequestComponentType.All,
          undefined,
          undefined,
          pageParam.cursor,
          CHANGES_PAGE_SIZE,
          false,
          pageParam.pinnedToken,
        ),
      initialPageParam: { cursor: null, pinnedToken: null } as ChangesPageParam,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.hasMore && lastPage.nextCursor
          ? {
              cursor: lastPage.nextCursor,
              // null falls back to BE current HEAD; backend guarantees non-null on first page
              pinnedToken: allPages[0]?.headPairToken ?? null,
            }
          : undefined,
      enabled: !!systemId && !!id,
      staleTime: DEFAULT_CACHE_TIME,
    }),
  events: (systemId: string, id: ChangeRequestId) =>
    infiniteQueryOptions({
      queryKey: changeRequestQueryKeys.events(systemId, id),
      queryFn: ({ pageParam }) => clientSDK.listChangeRequestEvents(systemId, id, pageParam, EVENTS_PAGE_SIZE),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
      enabled: !!systemId && !!id,
      staleTime: DEFAULT_CACHE_TIME,
    }),
  changesProbe: (systemId: string, id: ChangeRequestId) =>
    queryOptions({
      queryKey: changeRequestQueryKeys.changesProbe(systemId, id),
      // Polls every 60s (foreground only) to detect when source/target branches advance;
      // allowHeadRefresh=true returns the latest headPairToken regardless of cursor, so a
      // token mismatch against the pinned infinite-query token signals staleness.
      queryFn: () => clientSDK.changeRequestChanges(systemId, id, undefined, undefined, undefined, null, 1, true, null),
      enabled: !!systemId && !!id,
      staleTime: 0,
      refetchInterval: QUERY_REFETCH_INTERVAL_MS_60S,
      refetchIntervalInBackground: false,
    }),
  compareInfinite: (systemId: string, sourceSnapshotId: string, targetSnapshotId: string) =>
    infiniteQueryOptions({
      queryKey: changeRequestQueryKeys.compare(systemId, sourceSnapshotId, targetSnapshotId),
      // Immutable snapshot IDs mean the diff is deterministic across pages, so no head-pair
      // token plumbing is needed (unlike changesInfinite).
      queryFn: ({ pageParam }) =>
        clientSDK.compareSnapshots(
          systemId,
          sourceSnapshotId,
          targetSnapshotId,
          ChangeRequestComponentType.All,
          undefined,
          null,
          pageParam,
          CHANGES_PAGE_SIZE,
        ),
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => (lastPage.hasMore && lastPage.nextCursor ? lastPage.nextCursor : undefined),
      enabled: !!systemId && !!sourceSnapshotId && !!targetSnapshotId,
      staleTime: DEFAULT_CACHE_TIME,
    }),
};

function statusFilterToSdk(status: "open" | "merged" | "closed"): "OPEN" | "MERGED" | "CLOSED" {
  switch (status) {
    case "open":
      return "OPEN";
    case "merged":
      return "MERGED";
    case "closed":
      return "CLOSED";
  }
}
